import { QuickstartTheme } from '../../types/theme';
import { ResolvedDeviceFallbackConfig } from '../../types/deviceFallbackTheme';
import { ThankYouCopy, ThankYouStoreLineDesktop } from '../QuickstartThankYouCopy';
import { ThemeLogo } from '../ThemeLogo';
import { FallbackStageMedia } from './FallbackStageMedia';
import { SweepingQrRim } from './SweepingQrRim';
import { useThankYouQrCode } from './useThankYouQrCode';
import { withAlpha } from './fallbackColor';
import {
  DESKTOP_MEDIA_ZOOM,
  DESKTOP_PX,
  MEDIA_PANEL_WIDTH,
  PANEL_EDGE_ALPHA,
  PANEL_TINT_ALPHA,
  QR_CARD_PX,
  SCRIM_BLEED_PX,
  SCRIM_OVERHANG,
  SCRIM_WIDTH,
  buildCopyScrim,
  buildCopyScrimMask,
  desktopBlur,
  desktopPx,
  desktopScaled,
} from './desktopFallbackScale';

interface DesktopThankYouStageProps {
  theme: QuickstartTheme;
  config: ResolvedDeviceFallbackConfig;
  /** Outbound CTA URL rendered as the gold plate's real anchor href (WebView-safe). */
  ctaHref?: string;
  /** Analytics callback fired on CTA click before the anchor navigates. */
  onCta?: () => void;
  /** Resolved theme copy (headline, subcopy, CTA label, eyebrow). */
  copy: ThankYouCopy;
}

/**
 * Thank-you / join-failure screen, desktop shell.
 *
 * The DeviceFallback desktop layout - split stage with the campaign photograph
 * running under both halves - with the scan panel's QR station replaced by the
 * store CTA. The story side keeps the design's structure: brand stack top-left,
 * copy foot-pinned over the scrim wedge, powered-by mark in the corner.
 */
export const DesktopThankYouStage = ({ theme, config, ctaHref, onCta, copy }: DesktopThankYouStageProps) => {
  const { qrDataUrl } = useThankYouQrCode({ ctaUrl: ctaHref });
  const scrimBleed = desktopPx(SCRIM_BLEED_PX);
  const scrimMask = buildCopyScrimMask(scrimBleed);

  return (
    <div
      className='relative flex min-h-screen w-full items-center justify-center overflow-hidden font-["Inter"]'
      style={{ backgroundColor: config.stageColor }}
    >
      <div
        className='relative shrink-0 overflow-hidden'
        style={{
          // The window, whatever shape it is - holding the stage to 16:9 instead
          // bands the top and bottom with flat stage colour on taller windows.
          width: '100vw',
          height: '100vh',
          // Type still tracks the artboard rather than the window: whichever
          // dimension is binding sets the scale.
          ['--stage-w' as string]: 'min(100vw, calc(100vh * 16 / 9))',
        }}
      >
        {/* The campaign, across the whole stage rather than only the story side,
          so the two halves are one photograph with a pane over half of it. */}
        <FallbackStageMedia
          theme={theme}
          blur={desktopBlur(config.backgroundBlurPx)}
          zoom={DESKTOP_MEDIA_ZOOM}
        />

        {/* The action panel, as glass over the same photograph: a second copy of
          the media blurred harder and tinted, clipped to the right of the split.
          Composited rather than backdrop-filtered so it stays identical in
          browsers that decline backdrop filters. */}
        <div
          className='pointer-events-none absolute inset-0'
          style={{ clipPath: `inset(0 0 0 ${MEDIA_PANEL_WIDTH})` }}
        >
          <FallbackStageMedia
            theme={theme}
            blur={desktopBlur(config.panelBlurPx)}
            zoom={DESKTOP_MEDIA_ZOOM}
          />
          <div
            className='absolute inset-0'
            style={{ backgroundColor: withAlpha(config.stageColor, PANEL_TINT_ALPHA) }}
          />
        </div>

        {/* The join, drawn rather than left to chance. */}
        <div
          className='pointer-events-none absolute inset-y-0'
          style={{
            left: MEDIA_PANEL_WIDTH,
            width: '1px',
            backgroundColor: withAlpha(config.accentColor, PANEL_EDGE_ALPHA),
          }}
        />

        <div className='relative flex h-full w-full'>
          {/* Story side: brand stack pinned to the top, copy pushed to the foot. */}
          <div
            className='relative flex h-full flex-col overflow-hidden'
            style={{ width: MEDIA_PANEL_WIDTH }}
          >
            <div
              className='flex min-w-0 max-w-full flex-col items-center'
              style={{
                padding: `${desktopPx(DESKTOP_PX.logoStackTop)} ${desktopPx(DESKTOP_PX.gutter)} 0`,
                gap: desktopPx(DESKTOP_PX.logoGap),
                alignSelf: 'flex-start',
              }}
            >
              {!theme.hideStudioLogoOnCTA && (
                <ThemeLogo kind='thankYouStudio' theme={theme} style={{ height: desktopScaled(config.desktopStudioLogoScale) }} />
              )}
              {!theme.hideGameLogoOnCTA && (
                <ThemeLogo kind='game' theme={theme} style={{ height: desktopScaled(config.desktopGameLogoScale) }} />
              )}
            </div>

            <div className='flex min-h-0 flex-1 flex-col justify-end'>
              <div className='relative w-full'>
                {/* The wedge under the copy, sized by the copy rather than by a
                  guess. Ramp and mask are the canvas's own - see
                  desktopFallbackScale for why each axis is independent. */}
                <div
                  aria-hidden
                  className='pointer-events-none absolute'
                  style={{
                    top: `calc(-1 * ${SCRIM_OVERHANG})`,
                    left: `calc(-1 * ${scrimBleed})`,
                    bottom: `calc(-1 * ${scrimBleed})`,
                    width: `calc(${SCRIM_WIDTH} + ${scrimBleed})`,
                    background: buildCopyScrim(config.stageColor),
                    maskImage: scrimMask,
                    WebkitMaskImage: scrimMask,
                    filter: `blur(${desktopPx(DESKTOP_PX.scrimBlur)})`,
                  }}
                />

                <div
                  className='relative'
                  style={{
                    padding: `${desktopPx(DESKTOP_PX.copyTop)} ${desktopPx(DESKTOP_PX.gutter)} ${desktopPx(DESKTOP_PX.copyBottom)}`,
                    maxWidth: desktopPx(DESKTOP_PX.copyMaxWidth),
                  }}
                >
                  <h1
                    className='font-bold leading-tight'
                    style={{ color: config.qrRimStartColor, fontSize: desktopPx(25) }}
                  >
                    {copy.headline}
                  </h1>
                  <ThankYouStoreLineDesktop gameName={copy.gameName} storePlatform={copy.storePlatform} accentColor={copy.accentColor} />
                  {copy.message ? (
                    <p
                      className='relative text-center'
                      style={{ marginTop: desktopPx(8), color: config.copyColor, fontSize: desktopPx(12) }}
                    >
                      {copy.message}
                    </p>
                  ) : null}
                  <div
                    className='flex items-end'
                    style={{
                      gap: desktopPx(DESKTOP_PX.poweredByGap),
                      marginTop: desktopPx(DESKTOP_PX.poweredByOffset),
                      // Holds the old footprint so the headline and subcopy above stay put.
                      height: desktopPx(DESKTOP_PX.poweredBySlot),
                    }}
                  >
                    <ThemeLogo kind='poweredBy' theme={theme} style={{ height: desktopPx(DESKTOP_PX.poweredBy) }} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action station. The glass behind it is painted at stage level so it
            can span the full height; this layer stacks the QR code (framed by
            the Icon Drop sweeping rim), the store CTA under it, and the
            availability eyebrow under that. */}
          <div
            className='relative flex flex-1 flex-col items-center justify-center'
            style={{
              gap: desktopPx(DESKTOP_PX.qrCaptionGap),
              padding: `${desktopPx(DESKTOP_PX.scanPanelPaddingY)} ${desktopPx(DESKTOP_PX.scanPanelPaddingX)}`,
            }}
          >
            {ctaHref && onCta ? (
              <>
                <SweepingQrRim startColor={config.qrRimStartColor} endColor={config.qrRimEndColor}>
                  <div
                    className='flex items-center justify-center'
                    style={{
                      width: desktopPx(QR_CARD_PX),
                      height: desktopPx(QR_CARD_PX),
                      // Half the canvas card's padding: that card's inset was
                      // part of the off-white design, where the border showed.
                      // White-on-white it is pure quiet zone, so spending less
                      // of it lets the code read larger in the same frame.
                      padding: desktopPx(8),
                      borderRadius: desktopPx(DESKTOP_PX.qrCardRadius),
                      // Pure white, matching the QR SVG's own background: the
                      // design's off-white card put a hard edge and a pink
                      // fringe around the code, which read as a rendering
                      // artefact. White-on-white hides the seam and reads as
                      // one continuous quiet zone.
                      backgroundColor: '#FFFFFF',
                    }}
                  >
                    {qrDataUrl ? (
                      <img
                        src={qrDataUrl}
                        alt={`QR code to open ${ctaHref}`}
                        className='h-full w-full object-contain'
                      />
                    ) : (
                      <div className='h-full w-full animate-pulse rounded-lg bg-neutral-200' />
                    )}
                  </div>
                </SweepingQrRim>

                <a
                  href={ctaHref}
                  target='_self'
                  rel='noopener noreferrer'
                  className='flex items-center justify-center text-center font-bold no-underline'
                  style={{
                    height: desktopPx(56),
                    borderRadius: desktopPx(15),
                    backgroundColor: config.qrRimStartColor,
                    color: config.stageColor,
                    fontSize: desktopPx(21),
                    lineHeight: 1,
                    boxSizing: 'border-box',
                    paddingInline: desktopPx(20),
                    appearance: 'none' as const,
                    WebkitAppearance: 'none' as const,
                  }}
                  onClick={onCta}
                >
                  {copy.ctaLabel}
                </a>
              </>
            ) : (
              <div
                className='flex items-center justify-center text-center font-bold'
                style={{
                  height: desktopPx(56),
                  borderRadius: desktopPx(15),
                  backgroundColor: config.qrRimStartColor,
                  color: config.stageColor,
                  fontSize: desktopPx(21),
                  lineHeight: 1,
                  boxSizing: 'border-box',
                  paddingInline: desktopPx(20),
                }}
              >
                {copy.ctaLabel}
              </div>
            )}

            {copy.eyebrow ? (
                  <p
                    className='relative text-center'
                    style={{
                      color: '#FFFFFF',
                      fontSize: desktopPx(DESKTOP_PX.qrCaption),
                      fontWeight: 500,
                      letterSpacing: '0.08em',
                    }}
                  >
                    {copy.eyebrow}
                  </p>
                ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};